import { Utils } from "src/modules/utils/shared/utils";
import { FrontendFieldDefinition } from "./frontend-field-definition.model";
import { FrontendFieldType } from "./frontend-field-type.enum";
import { TableCellType } from "./table-cell-type.enum";
import { TableCell } from "./table-cell.model";
import { TableData } from "./table-data.model";
import { TableSizer } from "./table-sizer.model";
import { TableCellEvent } from "./table-cell-event.model";
import { TableRow } from "./table-row.model";
import { Type } from "class-transformer";

export class TableColumn {

    id = "";
    caption: string;
    icon: string;
    groupCaption: string;
    cellType = TableCellType.text;
    resizable = true;
    sortable = true;
    filterable = true;
    minWidth: string = null;
    maxWidth: string = null;
    autoWidth: number = null;
    flex: number = null;
    editable = false;
    editableMaxLength: number = null;
    isLink = false;
    frozen: "left" | "right" | null = null;
    rotate90 = false;
    keepHtml = false;
    toolTip: string = null;
    numDecimalDigits: number = null;
    expandDecimalDigits = false;
    @Type(() => FrontendFieldDefinition)
    editor: FrontendFieldDefinition | ((TableCellEvent) => FrontendFieldDefinition);
    styleClass = "";
    useHeaderWidthAsMin = true;

    cellRenderer: (params: any) => string;
    valueFormatter: (value: any) => string;
    valueGetter: (value: any) => any;
    valueSetter: (value: TableCellEvent) => void;
    toolTipGetter: (value: TableCellEvent) => string;
    editableCallback: (value: TableCellEvent) => boolean;
    getStyleClassCallback: (value: TableCellEvent) => string;
    headerClicked: (columnId: string) => boolean | Promise<boolean>;

    calculatedWidth: string = null;

    constructor(id: string, caption: string, cellType = TableCellType.text, props: Partial<TableColumn> = null) {
        this.id = id;
        this.caption = caption;
        this.cellType = cellType;
        if (props != null) {
            Object.assign(this, props);
        }
        if (this.id != null && this.autoWidth == null && this.flex == null) {
            console.trace("Warnung. Tabellenspalte " + id + "/" + caption + ": Keine Breite und kein Flex angegeben");
        }
    }

    getEditor(getEventCallback: () => TableCellEvent): FrontendFieldDefinition {
        return Utils.isFunction(this.editor) ? (this.editor as any)(getEventCallback()) : this.editor;                        
    }

    getRealWidth(table: TableData, byContent: boolean): {width: number; flex: number} {
        return new TableSizer().calculate(table, this, byContent, this.useHeaderWidthAsMin);
    }

    isEditable(event: TableCellEvent): boolean {
        return this.editable && (this.editableCallback == null || this.editableCallback(event));
    }

    getValue(value: any): string {
        return this.valueGetter != null ? this.valueGetter(value) : value;
    }

    setValue(event: TableCellEvent): void {
        if (this.valueSetter != null) {
            this.valueSetter(event);
        }
        else {
            if (event.row.values[event.column.colId] instanceof TableCell) {
                event.row.values[event.column.colId].value = event.value;
            } 
            else {
                event.row.values[event.column.colId] = event.value;
            }
        }
    }

    formatValue(value: any, locale = "de"): string {
        if (this.valueFormatter != null) {
            return this.valueFormatter(value);
        }
        switch (this.cellType) {
            case TableCellType.date:
                return Utils.isNoe(value) ? "" : Utils.dateFormatDefaultDate(Utils.toDate(value));
            case TableCellType.dateTime:
                return Utils.isNoe(value) ? "" : Utils.dateFormatDefault(Utils.toDate(value));
            case TableCellType.duration:
                return value == null ? "" : Utils.toDurationStringAdvanced(Utils.toNumber(value), 2, true);
            case TableCellType.durationNanos:
                return value == null ? "" : Utils.toDurationStringAdvancedNanos(Utils.toNumber(value), 2, true);
            case TableCellType.byteSize:
                return value == null ? "" : Utils.toByteSizeString(Utils.toNumber(value), false);
            case TableCellType.int:
                return Utils.isInt(value) ? Utils.formatNumber(Utils.toNumber(value), 0, false, true, locale != "de") : value;
            case TableCellType.decimal:
                return Utils.isNumber(value) ? Utils.formatNumber(Utils.toNumber(value), this.numDecimalDigits ?? 10, this.expandDecimalDigits, true, locale != "de") : value;
            case TableCellType.button:
                return "";
            default:
                return value;
        }
    }

    toCsvString(value: any): string {
        value = TableCell.getValue(value);
        switch (this.cellType) {
            case TableCellType.date:
                return Utils.dateFormat(value as Date, "dd.mm.yyyy");
            case TableCellType.dateTime:
                return Utils.dateFormatDefault(value as Date);
            case TableCellType.yesNo:
                return Utils.toBool(value) ? "Ja" : "Nein";
            default:
                return Utils.toString(value);
        }
    }

    getFullCaption(): string {
        return (Utils.isNoe(this.groupCaption) ? "" : this.groupCaption + " ") + this.caption;
    }

    getWidth(): string {
        return this.calculatedWidth;
    }

    getMinWidth(): string {
        return this.minWidth ?? this.getWidth();
    }

    getMaxWidth(): string {
        return this.maxWidth ?? this.getWidth();
    }

    toFrontendField(): FrontendFieldDefinition {
        let result = new FrontendFieldDefinition(this.id, this.caption, FrontendFieldType.text, { enabled: true, maxLength: this.editableMaxLength });
        switch (this.cellType) {
            case TableCellType.text:
                break;
            case TableCellType.int:
                result.type = FrontendFieldType.int;
                break;
            case TableCellType.decimal:
                result.type = FrontendFieldType.decimal;
                break;
            case TableCellType.yesNo:
                result.type = FrontendFieldType.checkBox;
                break;
            case TableCellType.date:
                result.type = FrontendFieldType.datePicker;
                break;
            case TableCellType.dateTime:
                result.type = FrontendFieldType.datePicker;
                break;
            default:
                throw new Error("Der Zelltyp " + this.cellType + " kann nicht in FrontendFieldType konvertiert werden");
        }
        return result;
    }

}
